import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Chip,
  Grid,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  LocalHospital as SurgeryIcon,
  CalendarToday as CalendarIcon,
  Assignment as OrderIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import SurgicalPreOpPhase from '../Procedures/components/SurgicalPreOpPhase';
import SurgicalIntraOpPhase from '../Procedures/components/SurgicalIntraOpPhase';
import SurgicalPACUPhase from '../Procedures/components/SurgicalPACUPhase';
import SurgicalPostOpPhase from '../Procedures/components/SurgicalPostOpPhase';

const phaseTabs = [
  { label: 'Pre-Op', key: 'preOp' },
  { label: 'Intra-Op', key: 'intraOp' },
  { label: 'PACU/Recovery', key: 'pacu' },
  { label: 'Post-Op/Discharge', key: 'postOp' },
];

const SurgicalCaseDialog = ({ open, onClose, order, surgicalCase }) => {
  const [activePhase, setActivePhase] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActivePhase(newValue);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date.toDate ? date.toDate() : new Date(date);
      return format(dateObj, 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const getPhaseStatus = (phase) => {
    if (!surgicalCase?.phases?.[phase]) return 'pending';
    return surgicalCase.phases[phase].status || 'pending';
  };

  const getPhaseColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  if (!order) return null;

  // Transform order data to match SurgicalProcedurePanel expected format
  const surgicalProcedure = {
    id: order.id,
    procedureType: order.testCode || order.testType || 'Surgical Procedure',
    specialty: order.specialty || 'General Surgery',
    status: order.status || 'ordered',
    orderDate: order.orderDate,
    notes: order.notes,
    estimatedDuration: order.estimatedDuration || 120,
    priority: order.priority || 'Normal',
    orderingProvider: order.orderingProvider
  };

  const patient = {
    id: order.patientId,
    name: order.patientName,
    mrn: order.patientMRN,
    gender: order.patientGender,
    dateOfBirth: order.patientDOB
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, minHeight: '80vh' }
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 2,
          background: 'linear-gradient(135deg, #6B46C1 0%, #9333EA 25%, #3B82F6 75%, #1E40AF 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SurgeryIcon sx={{ fontSize: 32, color: 'white' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                Surgical Case Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {order.patientName} • {order.patientMRN}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="large" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 0 }}>
        {/* Case Summary */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">Patient:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.patientName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OrderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">MRN:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.patientMRN}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">Order Date:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(order.orderDate)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SurgeryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">Procedure:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.testCode || order.testType || 'Surgical Procedure'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Provider:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.orderingProvider || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip
                      label={surgicalCase?.status || 'Started'}
                      color={surgicalCase?.status === 'completed' ? 'success' : 'primary'}
                      size="small"
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Phase Tabs */}
        <Paper elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
          <Tabs
            value={activePhase}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              pt: 2,
              '& .MuiTab-root': {
                minHeight: 48,
                fontWeight: 500,
                textTransform: 'none',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#708bff',
                height: 3,
              },
            }}
          >
            {phaseTabs.map((tab, idx) => (
              <Tab 
                key={tab.label} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.label}
                    <Chip
                      label={getPhaseStatus(tab.key)}
                      color={getPhaseColor(getPhaseStatus(tab.key))}
                      size="small"
                      sx={{ ml: 1, fontSize: '0.75rem' }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* Phase Content */}
        <Box sx={{ minHeight: '400px' }}>
          {activePhase === 0 && (
            <SurgicalPreOpPhase 
              surgicalProcedure={surgicalProcedure} 
              patient={patient} 
              encounterId={order.encounterId}
            />
          )}
          {activePhase === 1 && (
            <SurgicalIntraOpPhase 
              surgicalProcedure={surgicalProcedure} 
              patient={patient} 
              encounterId={order.encounterId}
            />
          )}
          {activePhase === 2 && (
            <SurgicalPACUPhase 
              surgicalProcedure={surgicalProcedure} 
              patient={patient} 
              encounterId={order.encounterId}
            />
          )}
          {activePhase === 3 && (
            <SurgicalPostOpPhase 
              surgicalProcedure={surgicalProcedure} 
              patient={patient} 
              encounterId={order.encounterId}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurgicalCaseDialog; 